'use client';

import { useState, useMemo, useCallback } from 'react';
import { LayoutGroup, motion } from 'framer-motion';
import { size } from 'lodash';
import { useMediaQuery } from 'usehooks-ts';
import type { HeroCarrouselBanner } from '@/graphql/queries/pages/home/types';
import { useAnimationStore } from '@/stores/animationStore';
import { Arrows } from './Arrows';
import { Media } from './Media';
import { ProgressBar } from './ProgressBar';
import { Text } from './Text';
import { animationBannerVariant } from './variants';

type AnimatedHeroCarouselProps = {
  disabledAnimation?: boolean;
  defaultIsMobile: boolean;
  items: HeroCarrouselBanner;
};

export const AnimatedHeroCarousel = ({
  items,
  defaultIsMobile,
  disabledAnimation = false
}: AnimatedHeroCarouselProps) => {
  const changeStartBannerAnimation = useAnimationStore((store) => store.changeStartBannerAnimation);
  const startBannerAnimation = useAnimationStore((store) => store.startBannerAnimation);

  const [animationBannerEnd, setAnimationBannerEnd] = useState(disabledAnimation);
  const [activeIndex, setActiveIndex] = useState(0);

  const isMobile = useMediaQuery('(max-width: 1023px)', {
    defaultValue: defaultIsMobile
  });

  const totalItems = size(items.slides);

  const initialVariant = useMemo(() => {
    if (isMobile || disabledAnimation) {
      return {
        initial: false,
        animate: false
      };
    }

    if (startBannerAnimation) {
      return animationBannerVariant;
    }

    return {
      initial: animationBannerVariant.initial,
      animate: false
    };
  }, [isMobile, startBannerAnimation, disabledAnimation]);

  const slides = items.slides;

  const nextSlideHandler = useCallback(() => {
    setActiveIndex((prevIndex) => (prevIndex === totalItems - 1 ? 0 : prevIndex + 1));
  }, [totalItems]);

  const previousSlideHandler = useCallback(() => {
    setActiveIndex((prevIndex) => (prevIndex === 0 ? totalItems - 1 : prevIndex - 1));
  }, [totalItems]);

  const renderContentCarousel = useCallback(() => {
    return (
      <>
        <div className="flex h-full flex-col justify-end gap-2 bg-blue-400">
          {(isMobile || animationBannerEnd) && (
            <Text
              activeIndex={activeIndex}
              banner={slides[activeIndex].banner}
              onNext={nextSlideHandler}
              onPrevious={previousSlideHandler}
              activeDrag={totalItems > 1}
            />
          )}
          {totalItems > 1 && (
            <div className="flex flex-col items-start gap-4 px-6 pb-10 lg:w-full lg:px-12 lg:pb-12 2xl:gap-7">
              <Arrows onNext={nextSlideHandler} onPrevious={previousSlideHandler} />
              <ProgressBar
                enabled={isMobile || animationBannerEnd}
                activeIndex={activeIndex}
                totalItems={totalItems}
                onNextSlide={nextSlideHandler}
              />
            </div>
          )}
        </div>
        <Media activeIndex={activeIndex} media={slides[activeIndex].banner.media} />
      </>
    );
  }, [
    isMobile,
    animationBannerEnd,
    totalItems,
    activeIndex,
    slides,
    nextSlideHandler,
    previousSlideHandler
  ]);

  if (totalItems === 0) return null;

  if (isMobile) {
    return (
      <div className="grid w-full grid-cols-1 grid-rows-[minmax(400px,max-content)_300px] items-center justify-between overflow-hidden">
        {renderContentCarousel()}
      </div>
    );
  }

  return (
    <LayoutGroup>
      <motion.div
        layout
        className="grid w-full items-center justify-between overflow-hidden lg:h-[calc(100vh-128px)] lg:grid-cols-2 lg:grid-rows-1"
        initial={initialVariant?.initial}
        animate={initialVariant?.animate}
        transition={{
          duration: 0.7
        }}
        onAnimationComplete={() => {
          changeStartBannerAnimation(false);
          setAnimationBannerEnd(true);
        }}
      >
        {renderContentCarousel()}
      </motion.div>
    </LayoutGroup>
  );
};
